import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  FeedbackState,
  Mono,
  Progress,
  Segmented,
  Skeleton,
  type DataTableColumn,
} from "@h2j-data-team/ui";
import type { ReloadCrowdedSlot, ReloadWaitCell } from "@repo/shared";

import { KpiCell, KpiStrip } from "@/components/app-analysis/kpi";
import { ListError } from "@/components/qvd-analysis/qvd-list-parts";
import { reloadBottlenecksQueryOptions } from "@/features/reloads/api";
import { useFormatDate } from "@/hooks/use-format-date";
import type { ReloadPeriodParams } from "@/lib/api/reloads";
import { formatSeconds } from "@/lib/duration-format";
import { tableMessages } from "@/lib/ui-labels";
import { useTableDensity } from "@/lib/use-table-density";

type HeatMetric = "p90" | "median" | "count";

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const BUCKET_OPACITY = [0.2, 0.4, 0.65, 0.95];
// A p90 over fewer reloads than this is noise, not a bottleneck.
const MIN_WINDOW_RELOADS = 5;
const HIGHLIGHTED_SLOTS = 2;
const ONE_DECIMAL = { minimumFractionDigits: 1, maximumFractionDigits: 1 };

const METRIC_VALUE: Record<HeatMetric, (cell: ReloadWaitCell) => number> = {
  p90: (cell) => cell.p90WaitSeconds,
  median: (cell) => cell.medianWaitSeconds,
  count: (cell) => cell.count,
};

const pad = (hour: number) => String(hour).padStart(2, "0");

const bucketColor = (bucket: number) =>
  `color-mix(in srgb, var(--color-primary) ${BUCKET_OPACITY[bucket] * 100}%, transparent)`;

// Four buckets relative to the metric's maximum; the muted cell is reserved for
// "no reloads", so even the lowest bucket is tinted.
const bucketOf = (value: number, max: number) =>
  max > 0
    ? Math.min(BUCKET_OPACITY.length - 1, Math.floor((value / max) * BUCKET_OPACITY.length))
    : 0;

const windowLabel = (t: TFunction<"reloads">, weekday: number, hour: number) =>
  t("bottlenecks.window", { day: t(`bottlenecks.weekdays.${weekday}`), hour: pad(hour) });

const secondsOrDash = (seconds: number | null) => (seconds === null ? "—" : formatSeconds(seconds));

export function BottlenecksView({
  connectionId,
  period,
  version,
}: {
  connectionId: string;
  period: ReloadPeriodParams;
  version: string;
}) {
  const { t, i18n } = useTranslation("reloads");
  const { formatDateTime } = useFormatDate();
  const { data, isPending, isError, error, refetch } = useQuery(
    reloadBottlenecksQueryOptions(connectionId, period, version),
  );

  if (isPending) return <BottlenecksSkeleton />;
  if (isError) {
    return (
      <ListError error={error} title={t("bottlenecks.loadFailed")} onRetry={() => void refetch()} />
    );
  }
  if (data.waitByHour.length === 0) {
    return <FeedbackState state="empty" title={t("bottlenecks.empty")} />;
  }

  const worst = data.waitByHour
    .filter((cell) => cell.count >= MIN_WINDOW_RELOADS)
    .reduce<
      ReloadWaitCell | undefined
    >((acc, cell) => (!acc || cell.p90WaitSeconds > acc.p90WaitSeconds ? cell : acc), undefined);

  return (
    <div className="flex flex-col gap-4">
      <KpiStrip>
        <KpiCell
          label={t("bottlenecks.kpi.median")}
          value={secondsOrDash(data.medianWaitSeconds)}
          subMeta={t("bottlenecks.kpi.medianSub")}
        />
        <KpiCell
          label={t("bottlenecks.kpi.p90")}
          value={secondsOrDash(data.p90WaitSeconds)}
          subMeta={t("bottlenecks.kpi.p90Sub")}
        />
        <KpiCell
          label={t("bottlenecks.kpi.peak")}
          value={data.peak ? data.peak.concurrency.toLocaleString(i18n.language) : "—"}
          subMeta={data.peak ? formatDateTime(data.peak.at) : undefined}
        />
        <KpiCell
          label={t("bottlenecks.kpi.worst")}
          value={worst ? windowLabel(t, worst.weekday, worst.hour) : "—"}
          subMeta={
            worst
              ? t("bottlenecks.kpi.worstSub", { p90: formatSeconds(worst.p90WaitSeconds) })
              : undefined
          }
        />
      </KpiStrip>
      <WaitHeatmapCard cells={data.waitByHour} tz={period.tz} worst={worst} />
      <CrowdedSlotsCard slots={data.crowdedSlots} />
    </div>
  );
}

function WaitHeatmapCard({
  cells,
  tz,
  worst,
}: {
  cells: ReloadWaitCell[];
  tz: string;
  worst: ReloadWaitCell | undefined;
}) {
  const { t, i18n } = useTranslation("reloads");
  const [metric, setMetric] = useState<HeatMetric>("p90");

  const valueOf = METRIC_VALUE[metric];
  const max = Math.max(0, ...cells.map(valueOf));
  const byWindow = new Map(cells.map((cell) => [cell.weekday * 24 + cell.hour, cell]));
  const legend = metric === "count" ? "count" : "wait";

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle asChild>
            <h2>{t("bottlenecks.heatmap.title")}</h2>
          </CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-dim">{t("bottlenecks.heatmap.timezone", { tz })}</span>
            <Segmented<HeatMetric>
              label={t("bottlenecks.heatmap.metricLabel")}
              size="compact"
              value={metric}
              onValueChange={setMetric}
              options={[
                { value: "p90", label: t("bottlenecks.heatmap.metric.p90") },
                { value: "median", label: t("bottlenecks.heatmap.metric.median") },
                { value: "count", label: t("bottlenecks.heatmap.metric.count") },
              ]}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-2.5">
        <div
          role="img"
          aria-label={
            worst
              ? t("bottlenecks.heatmap.ariaLabelWorst", {
                  window: windowLabel(t, worst.weekday, worst.hour),
                  p90: formatSeconds(worst.p90WaitSeconds),
                })
              : t("bottlenecks.heatmap.ariaLabel")
          }
          className="grid grid-cols-[2.25rem_repeat(24,minmax(0,1fr))] gap-[3px]"
        >
          <span />
          {HOURS.map((hour) => (
            <span key={hour} className="text-center font-mono text-[10.5px] text-dim">
              {hour % 3 === 0 ? pad(hour) : ""}
            </span>
          ))}
          {WEEKDAYS.map((weekday) => (
            <Fragment key={weekday}>
              <span className="flex items-center text-[11px] text-dim">
                {t(`bottlenecks.weekdays.${weekday}`)}
              </span>
              {HOURS.map((hour) => {
                const cell = byWindow.get(weekday * 24 + hour);
                return (
                  <div
                    key={hour}
                    className="h-[22px] rounded-[3px] bg-muted"
                    style={
                      cell
                        ? { backgroundColor: bucketColor(bucketOf(valueOf(cell), max)) }
                        : undefined
                    }
                    title={
                      cell
                        ? t("bottlenecks.heatmap.cell", {
                            count: cell.count,
                            formatted: cell.count.toLocaleString(i18n.language),
                            window: windowLabel(t, weekday, hour),
                            median: formatSeconds(cell.medianWaitSeconds),
                            p90: formatSeconds(cell.p90WaitSeconds),
                          })
                        : undefined
                    }
                  />
                );
              })}
            </Fragment>
          ))}
        </div>
        <div className="flex items-center justify-end gap-1.5 text-[11px] text-dim">
          <span>{t(`bottlenecks.heatmap.legend.${legend}.less`)}</span>
          {BUCKET_OPACITY.map((_, bucket) => (
            <span
              key={bucket}
              aria-hidden
              className="h-3 w-[18px] rounded-[3px]"
              style={{ backgroundColor: bucketColor(bucket) }}
            />
          ))}
          <span>{t(`bottlenecks.heatmap.legend.${legend}.more`)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function CrowdedSlotsCard({ slots }: { slots: ReloadCrowdedSlot[] }) {
  const { t, i18n } = useTranslation("reloads");
  const { density } = useTableDensity();

  // The API ranks the slots by how many reloads pile up; the card reads by how
  // long they wait, so the worst two sit on top and carry the warning tone.
  const rows = useMemo(
    () => [...slots].sort((a, b) => b.medianWaitSeconds - a.medianWaitSeconds),
    [slots],
  );
  const maxMedian = rows[0]?.medianWaitSeconds ?? 0;

  const columns = useMemo<DataTableColumn<ReloadCrowdedSlot>[]>(
    () => [
      {
        id: "time",
        header: t("bottlenecks.crowded.cols.time"),
        rowHeader: true,
        cell: (slot) => <Mono>{slot.time}</Mono>,
      },
      {
        id: "perDay",
        header: t("bottlenecks.crowded.cols.perDay"),
        width: 170,
        align: "end",
        variant: "numeric",
        cell: (slot) => slot.perDay.toLocaleString(i18n.language, ONE_DECIMAL),
      },
      {
        id: "median",
        header: t("bottlenecks.crowded.cols.median"),
        width: 150,
        align: "end",
        variant: "numeric",
        cell: (slot) => formatSeconds(slot.medianWaitSeconds),
      },
      {
        id: "relative",
        header: t("bottlenecks.crowded.cols.relative"),
        width: 220,
        // The wait is already in the previous column's text.
        cell: (slot) => (
          <Progress
            value={slot.medianWaitSeconds}
            max={maxMedian}
            tone={rows.indexOf(slot) < HIGHLIGHTED_SLOTS ? "warning" : "primary"}
            className="w-40"
            aria-hidden
          />
        ),
      },
    ],
    [t, i18n.language, rows, maxMedian],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle asChild>
          <h2>{t("bottlenecks.crowded.title")}</h2>
        </CardTitle>
        <CardDescription>{t("bottlenecks.crowded.note")}</CardDescription>
      </CardHeader>
      <DataTable
        label={t("bottlenecks.crowded.title")}
        bordered={false}
        density={density}
        columns={columns}
        rows={rows}
        getRowId={(slot) => slot.time}
        messages={tableMessages({ empty: { title: t("bottlenecks.crowded.empty") } })}
      />
    </Card>
  );
}

function BottlenecksSkeleton() {
  const { t } = useTranslation("common");
  return (
    <div className="flex flex-col gap-4" aria-busy>
      <p role="status" className="sr-only">
        {t("list.loading")}
      </p>
      <KpiStrip>
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-6 w-16" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </KpiStrip>
      <Card>
        <CardHeader>
          <Skeleton className="h-4 w-56" />
        </CardHeader>
        <CardContent className="flex flex-col gap-[3px]">
          {WEEKDAYS.map((weekday) => (
            <Skeleton key={weekday} className="h-[22px] w-full" />
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} className="h-3.5 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
